/**
 * Frontend Logger Utility
 *
 * Provides structured logging for React frontend with different log levels.
 *
 * Logging Strategy:
 * - Errors: Always logged (development + production) - critical for debugging
 * - Warnings: Always logged (development + production) - helps identify issues
 * - API Errors: Always logged (development + production) - critical for debugging
 * - Info/Debug: Development only - too verbose for production
 *
 * In production, errors/warnings can be sent to logging services (Sentry, LogRocket, etc.)
 * by configuring the logToService function.
 */

// Check environment - React apps set NODE_ENV at build time
// Fallback to checking if we're in a development environment
const environment =
  process.env.NODE_ENV ||
  (typeof window !== 'undefined' && window.location?.hostname === 'localhost'
    ? 'development'
    : 'production');
const isDevelopment = environment === 'development';

// Debug: Log environment detection (only once)
if (typeof window !== 'undefined' && !window.__loggerEnvLogged) {
  console.log(
    '[Logger] Environment detected:',
    environment,
    'isDevelopment:',
    isDevelopment
  );
  window.__loggerEnvLogged = true;
}

// Optional: Configure external logging service for production
// Example: Sentry, LogRocket, DataDog, etc.
const logToService = (level, message, data) => {
  // TODO: Configure external logging service here
  // Example:
  // if (window.Sentry && level === 'error') {
  //   window.Sentry.captureException(new Error(message), { extra: data });
  // }
};

/**
 * Format log message with timestamp
 */
function formatLog(level, message, data = null) {
  const timestamp = new Date().toISOString();

  if (isDevelopment) {
    // Human-readable format for development
    const dataStr = data ? ` | Data: ${JSON.stringify(data, null, 2)}` : '';
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${dataStr}`;
  }

  // In production, return structured data for potential logging services
  return {
    timestamp,
    level,
    message,
    ...(data && { data }),
  };
}

class Logger {
  /**
   * Info level logging (development only)
   * @param {string} message - Log message
   * @param {*} data - Optional data to log
   */
  static info(message, data = null) {
    if (isDevelopment) {
      console.log(formatLog('info', message, data));
    }
  }

  /**
   * Warning level logging (always logged, even in production)
   * @param {string} message - Log message
   * @param {*} data - Optional data to log
   */
  static warn(message, data = null) {
    const formatted = formatLog('warn', message, data);
    console.warn(formatted);

    // Send to external logging service in production
    if (!isDevelopment) {
      logToService('warn', message, data);
    }
  }

  /**
   * Error level logging (always logged, even in production)
   * Critical for debugging production issues
   * @param {string} message - Log message
   * @param {Error|*} error - Error object or error data
   * @param {*} context - Optional context data
   */
  static error(message, error, context = null) {
    const errorData = {
      message: error?.message || error,
      stack: error?.stack,
      ...(context && { context }),
    };
    const formatted = formatLog('error', message, errorData);
    console.error(formatted);

    // Send to external logging service in production
    if (!isDevelopment) {
      logToService('error', message, { error: errorData });
    }
  }

  /**
   * Debug level logging (only in development)
   * @param {string} message - Log message
   * @param {*} data - Optional data to log
   */
  static debug(message, data = null) {
    if (isDevelopment) {
      const formatted = formatLog('debug', message, data);
      // Use console.log instead of console.debug for better visibility
      // console.debug is often filtered out in browser DevTools
      console.log(formatted);
    } else {
      // In production, still log but with simpler format
      // This helps verify the logger is working even if environment detection fails
      console.debug(`[DEBUG] ${message}`, data || '');
    }
  }

  /**
   * API request logging (development only)
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {*} params - Query parameters
   * @param {*} body - Request body
   */
  static apiRequest(method, url, params = null, body = null) {
    if (isDevelopment) {
      const message = `API Request: ${method} ${url}`;
      const data = {
        ...(params && { params }),
        ...(body && { body }),
      };
      this.debug(message, Object.keys(data).length > 0 ? data : null);
    }
  }

  /**
   * API response logging
   * - Success responses (2xx, 3xx): Development only
   * - Error responses (4xx, 5xx): Always logged (treated as warnings/errors)
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {number} statusCode - Response status code
   * @param {*} responseData - Response data
   */
  static apiResponse(method, url, statusCode, responseData = null) {
    if (statusCode >= 400) {
      // Error responses: always log (as warning)
      const message = `API Response Error: ${method} ${url} - ${statusCode}`;
      this.warn(message, responseData);
    } else if (isDevelopment) {
      // Success responses: development only
      const message = `API Response: ${method} ${url} - ${statusCode}`;
      this.info(message, responseData);
    }
  }

  /**
   * API error logging (always logged, even in production)
   * Critical for debugging API integration issues
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {Error} error - Error object
   */
  static apiError(method, url, error) {
    const message = `API Error: ${method} ${url}`;
    const context = {
      url,
      method,
      response: error.response?.data,
      status: error.response?.status,
    };
    this.error(message, error, context);
  }

  /**
   * Component lifecycle logging (development only)
   * @param {string} componentName - Component name
   * @param {string} event - Lifecycle event (mount, unmount, update, etc.)
   * @param {*} details - Optional details
   */
  static component(componentName, event, details = null) {
    if (isDevelopment) {
      const message = `Component: ${componentName} - ${event}`;
      this.debug(message, details);
    }
  }

  /**
   * User action logging (development only)
   * @param {string} action - User action description
   * @param {*} data - Optional data
   */
  static userAction(action, data = null) {
    if (isDevelopment) {
      const message = `User Action: ${action}`;
      this.debug(message, data);
    }
  }

  /**
   * Performance metric logging (development only)
   * @param {string} operation - Operation name
   * @param {number} duration - Duration in milliseconds
   * @param {*} details - Optional details
   */
  static performance(operation, duration, details = null) {
    if (isDevelopment) {
      const message = `Performance: ${operation} took ${duration}ms`;
      const data = {
        duration,
        ...(details && { details }),
      };
      this.debug(message, data);
    }
  }
}

export default Logger;
